// Renders any server-embedded profile dump as a flame graph: the marked
// element holds a JSON `{ name, value (seconds), children }` tree as its text
// content and the chart is inserted right after it. `data-container-id` names
// the chart container for callers that need to address it (styles, specs).
import { SfLog } from '@app/adapters/logger'
import renderFlameGraph from '@app/shared/flame-graph/render-flame-graph'
import type { ProfileNode } from '@app/shared/flame-graph/flame-frames'

// The direct path for content injected outside a DOM-processor pass (async
// liquid updates): calling the default setup there instead would register a
// fresh onDomLoad handler per update and accumulate lifecycle listeners.
export const renderFlameGraphs = (root: ParentNode): void => {
  root
    .querySelectorAll<HTMLElement>("[data-behaviour='flame-graph']")
    .forEach((element) => {
      // Injected panes re-run DOM processors over content that may already
      // hold a rendered chart; a second pass must not add another one.
      if (element.dataset.flameGraphRendered) return

      const dump = element.textContent
      if (!dump) return
      element.dataset.flameGraphRendered = 'true'

      const container = document.createElement('div')
      if (element.dataset.containerId) {
        container.id = element.dataset.containerId
      }
      element.insertAdjacentElement('afterend', container)

      renderFlameGraph(container, JSON.parse(dump) as ProfileNode).catch(
        (error) => SfLog.error('Failed to render flame graph:', { error })
      )
    })
}

export default (): void => {
  window.onDomLoad(($dom) => renderFlameGraphs($dom[0]))
}
